/**
 * D-02 — the doctor's prescriptions: the ones they wrote and the paper ones a pharmacy digitised
 * under their name (read-only for them).
 *
 * Search, filters, sort and page size sit in a bar fixed above the list; the list below is a
 * server-driven virtual scroll that loads the next page as you near its end. Every control is
 * applied by the server to the whole list, not just the rows loaded so far. The filters live in
 * the URL (lib/doctorRxFilters.ts), so coming back from a prescription restores the list.
 */

import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";

import { listDoctorPrescriptions } from "../../api/doctor";
import type { DoctorRxSummary } from "../../api/doctor";
import { describeError } from "../../api/errors";
import { ExportButton } from "../../components/ui/ExportButton";
import { Select } from "../../components/ui/Select";
import {
  activeFilterCount,
  clearedFilters,
  filtersFromParams,
  filtersToParams,
  PER_PAGE_OPTIONS,
  SCHEDULE_OPTIONS,
  SORT_OPTIONS,
  SOURCE_OPTIONS,
  STATUS_OPTIONS,
  toApiQuery,
} from "../../lib/doctorRxFilters";
import type { DoctorRxFilters, RxSort } from "../../lib/doctorRxFilters";
import { DOCTOR_RX_CREATION_ENABLED } from "../../lib/features";
import { phoneLabel } from "../../lib/phone";
import {
  byPageNumber,
  EXPORT_PAGE_SIZE,
  sheetDateTime,
  sheetLabel,
  sheetPhone,
  type ExportColumn,
} from "../../lib/tableExport";
import { ScheduleBadge, StatusBadge } from "../patient/Prescriptions";

// Phones arrive masked to their last 4 digits for doctors (D-20), and are exported that way.
const EXPORT_COLUMNS: ExportColumn<DoctorRxSummary>[] = [
  { header: "Rx", value: (rx) => rx.prescription_id },
  { header: "Issued (IST)", value: (rx) => sheetDateTime(rx.issued_at) },
  { header: "Valid until (IST)", value: (rx) => sheetDateTime(rx.expires_at) },
  { header: "Patient", value: (rx) => rx.patient_name },
  { header: "For a family member", value: (rx) => rx.for_family_member_id !== null },
  { header: "Phone", value: (rx) => sheetPhone(rx.patient_phone) },
  { header: "Status", value: (rx) => sheetLabel(rx.status) },
  { header: "Schedule", value: (rx) => rx.schedule_class },
  { header: "Digitised from paper", value: (rx) => rx.is_digitized },
  { header: "Medicines", value: (rx) => rx.line_count },
  { header: "Notes", value: (rx) => rx.notes },
];

const ROW_H = 64; // estimate; rows are measured (mobile rows wrap onto more lines)
// Desktop columns: Rx · Patient · Medicines · Schedule · Status · Issued · Valid till.
const COLS =
  "md:grid md:grid-cols-[7.5rem_minmax(0,1fr)_6.5rem_7.5rem_5.5rem_6.5rem_6.5rem] md:items-center md:gap-3";

// The direction each sort reads most naturally in, applied when the sort changes.
const NATURAL_DESC: Record<RxSort, boolean> = { issued_at: true, expires_at: false, patient_name: false };

export function DoctorPrescriptions() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const setFilters = useCallback(
    (next: DoctorRxFilters) => setParams(filtersToParams(next), { replace: true }),
    [setParams],
  );
  const patch = (p: Partial<DoctorRxFilters>) => setFilters({ ...filters, ...p });

  // The search box types freely; the URL — and so the request — follows after a short pause.
  const [search, setSearch] = useState(filters.q);
  const [urlQ, setUrlQ] = useState(filters.q);
  if (filters.q !== urlQ) {
    // The URL's search changed: our own write (already in the box) or back/forward navigation.
    setUrlQ(filters.q);
    if (filters.q !== search.trim()) setSearch(filters.q);
  }
  useEffect(() => {
    const value = search.trim();
    if (value === filters.q) return;
    const timer = setTimeout(() => setFilters({ ...filters, q: value }), 300);
    return () => clearTimeout(timer);
  }, [search, filters, setFilters]);

  const clear = () => {
    setSearch("");
    setFilters(clearedFilters(filters));
  };

  const apiQuery = useMemo(() => toApiQuery(filters), [filters]);

  return (
    <div className="flex h-full min-h-[30rem] flex-col gap-4">
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Prescriptions</h2>
          <p className="text-sm text-gray-500">
            {DOCTOR_RX_CREATION_ENABLED ? "Prescriptions you've written, and paper" : "Paper"} prescriptions
            the pharmacy digitised under your name.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Every prescription the search and filters match, in the list's order. */}
          <ExportButton
            table="prescriptions"
            columns={EXPORT_COLUMNS}
            keyOf={(rx) => rx.id}
            disabled={apiQuery === null}
            fetchPage={byPageNumber((page) => listDoctorPrescriptions({ ...apiQuery, page, per_page: EXPORT_PAGE_SIZE }))}
          />
          {DOCTOR_RX_CREATION_ENABLED && (
            <Link to="/staff/doctor/create" className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:opacity-90">
              + Write Rx
            </Link>
          )}
        </div>
      </div>

      <FilterBar
        filters={filters}
        search={search}
        onSearch={setSearch}
        onChange={(p) => patch(p)}
        onSort={(sort) => patch({ sort, desc: NATURAL_DESC[sort] })}
        onClear={clear}
        rangeInvalid={apiQuery === null}
      />

      <RxList
        query={apiQuery}
        filtered={activeFilterCount(filters) > 0}
        onClear={clear}
        back={`${location.pathname}${location.search}`}
      />
    </div>
  );
}

// ---------------- Search + filters (fixed above the list) ----------------

function FilterBar({
  filters,
  search,
  onSearch,
  onChange,
  onSort,
  onClear,
  rangeInvalid,
}: {
  filters: DoctorRxFilters;
  search: string;
  onSearch: (value: string) => void;
  onChange: (patch: Partial<DoctorRxFilters>) => void;
  onSort: (sort: RxSort) => void;
  onClear: () => void;
  rangeInvalid: boolean;
}) {
  // Small screens show the search box and a Filters toggle; from md up everything is inline.
  const [open, setOpen] = useState(false);
  const active = activeFilterCount(filters);
  const narrowing = active - (filters.q ? 1 : 0);

  return (
    <section aria-label="Search and filter prescriptions" className="shrink-0 rounded-xl border bg-white p-3 text-sm shadow-xs">
      <div className="flex flex-wrap items-end gap-3">
        <Control label="Search" className="min-w-0 flex-1 md:min-w-[16rem]">
          <div className="relative">
            <SearchIcon />
            <input
              type="search"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Patient, phone, Rx no. or medicine…"
              aria-label="Search prescriptions"
              data-testid="rx-search"
              className="w-full rounded-lg border py-1.5 pl-9 pr-2 outline-hidden focus:border-brand focus:ring-1 focus:ring-brand"
            />
          </div>
        </Control>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="rx-filters"
          className={`rounded-lg border px-3 py-1.5 md:hidden ${narrowing ? "border-brand text-brand" : "text-gray-600"}`}
        >
          Filters{narrowing ? ` (${narrowing})` : ""}
        </button>

        <div id="rx-filters" className={`${open ? "grid" : "hidden"} w-full grid-cols-2 gap-3 md:contents`}>
          <FilterSelect label="Status" value={filters.status} options={STATUS_OPTIONS} onChange={(status) => onChange({ status })} />
          <FilterSelect label="Schedule" value={filters.schedule} options={SCHEDULE_OPTIONS} onChange={(schedule) => onChange({ schedule })} />
          <FilterSelect label="Source" value={filters.source} options={SOURCE_OPTIONS} onChange={(source) => onChange({ source })} />
          <Control label="Issued from">
            <input
              type="date"
              value={filters.from}
              max={filters.to || undefined}
              onChange={(e) => onChange({ from: e.target.value })}
              aria-invalid={rangeInvalid}
              className={`rounded-lg border px-2 py-1 ${rangeInvalid ? "border-red-500" : ""}`}
            />
          </Control>
          <Control label="Issued to">
            <input
              type="date"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(e) => onChange({ to: e.target.value })}
              aria-invalid={rangeInvalid}
              className={`rounded-lg border px-2 py-1 ${rangeInvalid ? "border-red-500" : ""}`}
            />
          </Control>
          <Control label="Sort by">
            <div className="flex">
              <Select value={filters.sort} options={SORT_OPTIONS} onChange={onSort} className="flex-1 rounded-r-none" />
              <button
                type="button"
                onClick={() => onChange({ desc: !filters.desc })}
                title={directionLabel(filters.sort, filters.desc)}
                aria-label={`Order: ${directionLabel(filters.sort, filters.desc)}. Click to reverse.`}
                className="select-addon"
              >
                {filters.desc ? "↓" : "↑"}
              </button>
            </div>
          </Control>
          <Control label="Per page">
            <Select
              value={filters.perPage}
              options={PER_PAGE_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
              onChange={(perPage) => onChange({ perPage })}
            />
          </Control>
          {active > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="self-end rounded-lg px-3 py-1.5 text-gray-500 hover:bg-gray-100 md:ml-auto"
            >
              Clear filters ({active})
            </button>
          )}
        </div>
      </div>
      {rangeInvalid && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          The “Issued from” date is after the “Issued to” date.
        </p>
      )}
    </section>
  );
}

function directionLabel(sort: RxSort, desc: boolean): string {
  switch (sort) {
    case "issued_at":
      return desc ? "Newest first" : "Oldest first";
    case "expires_at":
      return desc ? "Latest expiry first" : "Soonest expiry first";
    case "patient_name":
      return desc ? "Z to A" : "A to Z";
  }
}

// ---------------- The list (virtual scroll, pages fetched on demand) ----------------

function RxList({
  query,
  filtered,
  onClear,
  back,
}: {
  query: ReturnType<typeof toApiQuery>;
  filtered: boolean;
  onClear: () => void;
  back: string;
}) {
  const {
    data,
    error,
    isPending,
    isFetching,
    isPlaceholderData,
    isFetchNextPageError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["doctor-prescriptions", query],
    queryFn: ({ pageParam, signal }) => listDoctorPrescriptions({ ...query, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
    enabled: query !== null,
    // Keep showing the current rows while a new search / filter loads, instead of a blank list.
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);
  const total = data?.pages[0]?.total ?? 0;

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_H,
    getItemKey: (i) => rows[i]?.id ?? i,
    overscan: 8,
  });

  const items = virtualizer.getVirtualItems();
  const lastIndex = items.length ? items[items.length - 1].index : -1;
  useEffect(() => {
    // Ask for the next page a few rows before the end, so scrolling rarely waits.
    if (!isPlaceholderData && hasNextPage && !isFetchingNextPage && !isFetchNextPageError && lastIndex >= rows.length - 5) {
      void fetchNextPage();
    }
  }, [lastIndex, rows.length, hasNextPage, isFetchingNextPage, isFetchNextPageError, isPlaceholderData, fetchNextPage]);

  // A new search / filter starts at the top of its results.
  const queryId = JSON.stringify(query);
  useEffect(() => {
    parentRef.current?.scrollTo({ top: 0 });
  }, [queryId]);

  let body: ReactNode;
  if (query === null) {
    body = <Centered>Pick an “Issued from” date on or before the “Issued to” date.</Centered>;
  } else if (isPending) {
    body = (
      <div className="flex-1 space-y-2 p-4" aria-busy="true" aria-label="Loading prescriptions">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-10 animate-pulse rounded-sm bg-gray-100" />
        ))}
      </div>
    );
  } else if (error && rows.length === 0) {
    body = (
      <Centered>
        <span className="text-red-600">{describeError(error)}</span>
        <button type="button" onClick={() => void refetch()} className="text-sm font-medium text-brand hover:underline">
          Try again
        </button>
      </Centered>
    );
  } else if (rows.length === 0) {
    body = filtered ? (
      <Centered>
        <span>No prescriptions match these filters.</span>
        <button type="button" onClick={onClear} className="text-sm font-medium text-brand hover:underline">
          Clear filters
        </button>
      </Centered>
    ) : (
      <Centered>
        <span className="font-medium text-gray-600">No prescriptions yet.</span>
        <span className="max-w-sm">
          Paper prescriptions the pharmacy digitises under your name will appear here
          {DOCTOR_RX_CREATION_ENABLED ? ", with the ones you write" : ""}.
        </span>
      </Centered>
    );
  } else {
    body = (
      // The scroller is absolutely placed in the slot so a long list can't stretch the page:
      // the slot takes the height left under the filter bar, and only the rows scroll.
      <div className="relative min-h-0 flex-1">
        <div
          ref={parentRef}
          className={`absolute inset-0 overflow-auto transition-opacity ${isPlaceholderData ? "opacity-60" : ""}`}
          aria-busy={isPlaceholderData}
        >
          <ul style={{ height: virtualizer.getTotalSize(), position: "relative" }} aria-label="Prescriptions">
            {items.map((vi) => {
              const rx = rows[vi.index];
              return (
                <li
                  key={vi.key}
                  ref={virtualizer.measureElement}
                  data-index={vi.index}
                  className="border-b"
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${vi.start}px)` }}
                >
                  <RxRow rx={rx} back={back} />
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border bg-white shadow-xs">
      <div className={`hidden shrink-0 border-b bg-gray-50 px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-gray-500 ${COLS}`}>
        <span>Rx</span>
        <span>Patient</span>
        <span>Medicines</span>
        <span>Schedule</span>
        <span>Status</span>
        <span>Issued</span>
        <span>Valid till</span>
      </div>

      {body}

      <div className="flex shrink-0 items-center justify-between gap-3 border-t px-4 py-2 text-xs text-gray-500" aria-live="polite">
        <span>{data && query !== null ? `Showing ${rows.length} of ${total}` : ""}</span>
        {isPlaceholderData && isFetching ? (
          <span className="animate-pulse">Updating…</span>
        ) : isFetchingNextPage ? (
          <span className="animate-pulse">Loading more…</span>
        ) : isFetchNextPageError ? (
          <button type="button" onClick={() => void fetchNextPage()} className="font-medium text-red-600 hover:underline">
            Couldn't load more — try again
          </button>
        ) : hasNextPage ? (
          <span>Scroll for more</span>
        ) : (
          rows.length > 0 && <span>End of list</span>
        )}
      </div>
    </div>
  );
}

function RxRow({ rx, back }: { rx: DoctorRxSummary; back: string }) {
  const secondary = [rx.patient_phone ? phoneLabel(rx.patient_phone) : null, rx.notes].filter(Boolean).join(" · ");
  return (
    <Link
      to={`/staff/doctor/prescriptions/${rx.id}`}
      state={{ back }}
      className={`block px-4 py-3 text-sm transition-colors hover:bg-gray-50 focus-visible:bg-brand/5 focus-visible:outline-hidden ${COLS}`}
    >
      <div className="flex items-center gap-2 md:block">
        <span className="font-mono text-xs font-semibold text-gray-700">{rx.prescription_id}</span>
        {rx.is_digitized && (
          <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700 md:mt-1 md:block md:w-fit">
            Digitised
          </span>
        )}
        <span className="ml-auto text-xs text-gray-400 md:hidden">{formatDate(rx.issued_at)}</span>
      </div>
      <div className="mt-1 min-w-0 md:mt-0">
        <div className="truncate font-medium text-gray-800">{rx.patient_name || "Unnamed patient"}</div>
        {secondary && <div className="truncate text-xs text-gray-500">{secondary}</div>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 md:contents">
        <span className="text-xs text-gray-600 md:text-sm">
          {rx.line_count} medicine{rx.line_count === 1 ? "" : "s"}
        </span>
        <span>
          <ScheduleBadge cls={rx.schedule_class} />
        </span>
        <span>
          <StatusBadge status={rx.status} />
        </span>
        <span className="hidden text-gray-600 md:block">{formatDate(rx.issued_at)}</span>
        <span className="text-xs text-gray-500 md:text-sm md:text-gray-600">
          <span className="md:hidden">Valid till </span>
          {formatDate(rx.expires_at)}
        </span>
      </div>
    </Link>
  );
}

// ---------------- bits ----------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}


function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-gray-400">{children}</div>
  );
}

function Control({ label, children, className = "" }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`flex flex-col gap-1 text-xs text-gray-500 ${className}`}>
      {label}
      {children}
    </label>
  );
}

function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <Control label={label}>
      <Select value={value} options={options} onChange={onChange} data-testid={`rx-filter-${label.toLowerCase()}`} />
    </Control>
  );
}

function SearchIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
